import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

function ExpenseItems() {
  const { expenseId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemsMaster, setItemsMaster] = useState([]); // [{id, name, sku, uom}]
  const [rows, setRows] = useState([
    { id: crypto.randomUUID(), item_id: '', free_text_name: '', uom: 'each', qty: '', unit_price: '', type: 'Inventory' }
  ]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAdd, setQuickAdd] = useState({ name: '', sku: '', uom: 'each', category: 'Stationery', reorder_level: '' });

  const grandTotal = useMemo(() => {
    return rows.reduce((sum, r) => {
      const qty = Number(r.qty) || 0;
      const price = Number(r.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  }, [rows]);

  useEffect(() => {
    const load = async () => {
      try {
        // Load item master (inventory items)
        const res = await api.get('/api/inventory/items');
        setItemsMaster(res.data || []);
      } catch (e) {
        console.error('Failed to load items master', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows(prev => [...prev, { id: crypto.randomUUID(), item_id: '', free_text_name: '', uom: 'each', qty: '', unit_price: '', type: 'Inventory' }]);
  };

  const removeRow = (id) => {
    setRows(prev => (prev.length > 1 ? prev.filter(r => r.id !== id) : prev));
  };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: quickAdd.name.trim(),
        sku: quickAdd.sku.trim(),
        uom: quickAdd.uom.trim() || 'each',
        category: quickAdd.category,
        reorder_level: quickAdd.reorder_level ? Number(quickAdd.reorder_level) : null,
      };
      if (!payload.name) return;
      const res = await api.post('/api/inventory/items', payload);
      const created = res.data;
      setItemsMaster(prev => [created, ...prev]);
      // Select the newly created item on the last editable row
      setRows(prev => {
        const copy = [...prev];
        const idx = copy.findIndex(r => !r.item_id && r.type === 'Inventory');
        if (idx !== -1) copy[idx] = { ...copy[idx], item_id: created.id, free_text_name: '' };
        return copy;
      });
      setShowQuickAdd(false);
      setQuickAdd({ name: '', sku: '', uom: 'each', category: 'Stationery', reorder_level: '' });
    } catch (e) {
      console.error('Quick add failed', e);
      alert('Failed to add item.');
    }
  };

  const validateRows = () => {
    for (const r of rows) {
      const qty = Number(r.qty);
      const price = Number(r.unit_price);
      if (r.type === 'Inventory' && !r.item_id) return 'Please select an inventory item.';
      if (r.type !== 'Inventory' && !r.free_text_name.trim()) return 'Please enter item name for non-inventory row.';
      if (!(qty > 0)) return 'Quantity must be greater than zero.';
      if (!(price >= 0)) return 'Unit price must be zero or greater.';
    }
    return null;
  };

  const handleSave = async () => {
    const error = validateRows();
    if (error) return alert(error);
    setSaving(true);
    try {
      const payload = {
        lines: rows.map(r => ({
          item_id: r.type === 'Inventory' ? r.item_id : null,
          free_text_name: r.type !== 'Inventory' ? r.free_text_name.trim() : null,
          uom: r.uom,
          ordered_qty: Number(r.qty),
          unit_price: Number(r.unit_price),
          type: r.type,
        }))
      };
      await api.post(`/api/expenses/${encodeURIComponent(expenseId)}/items`, payload);
      alert('Items saved');
    } catch (e) {
      console.error('Save failed', e);
      alert('Failed to save items');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" style={{ color: 'var(--text)' }}>
      <h1 className="text-xl font-bold">Expense Items</h1>
      <p className="text-sm" style={{ color: 'var(--muted)' }}>
        Add line items for this expense. Expense ID: <strong>{expenseId}</strong>
      </p>

      <div className="p-4 rounded border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ color: 'var(--muted)' }}>
                  <th className="text-left py-2 pr-2">Type</th>
                  <th className="text-left py-2 pr-2">Item</th>
                  <th className="text-left py-2 pr-2">UOM</th>
                  <th className="text-right py-2 pr-2">Qty</th>
                  <th className="text-right py-2 pr-2">Unit Price</th>
                  <th className="text-right py-2 pr-2">Line Total</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const qty = Number(r.qty) || 0;
                  const price = Number(r.unit_price) || 0;
                  const lineTotal = qty * price;
                  return (
                    <tr key={r.id}>
                      <td className="py-1 pr-2">
                        <select
                          value={r.type}
                          onChange={(e) => updateRow(r.id, { type: e.target.value, item_id: '', free_text_name: '' })}
                          className="border rounded px-2 py-1"
                          style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        >
                          <option>Inventory</option>
                          <option>Non-Inventory</option>
                        </select>
                      </td>
                      <td className="py-1 pr-2">
                        {r.type === 'Inventory' ? (
                          <div className="flex gap-2 items-center">
                            <select
                              value={r.item_id}
                              onChange={(e) => updateRow(r.id, { item_id: e.target.value })}
                              className="border rounded px-2 py-1 w-64"
                              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                            >
                              <option value="">Select item…</option>
                              {itemsMaster.map((it) => (
                                <option key={it.id} value={it.id}>{it.name} {it.sku ? `(${it.sku})` : ''}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="px-2 py-1 border rounded"
                              style={{ borderColor: 'var(--border)' }}
                              onClick={() => setShowQuickAdd(true)}
                            >
                              + Quick Add
                            </button>
                          </div>
                        ) : (
                          <input
                            value={r.free_text_name}
                            onChange={(e) => updateRow(r.id, { free_text_name: e.target.value })}
                            placeholder="Describe item"
                            className="border rounded px-2 py-1 w-64"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                          />
                        )}
                      </td>
                      <td className="py-1 pr-2">
                        <input
                          value={r.uom}
                          onChange={(e) => updateRow(r.id, { uom: e.target.value })}
                          className="border rounded px-2 py-1 w-24"
                          style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        />
                      </td>
                      <td className="py-1 pr-2 text-right">
                        <input
                          type="number"
                          min="0"
                          value={r.qty}
                          onChange={(e) => updateRow(r.id, { qty: e.target.value })}
                          className="border rounded px-2 py-1 w-28 text-right"
                          style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        />
                      </td>
                      <td className="py-1 pr-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={r.unit_price}
                          onChange={(e) => updateRow(r.id, { unit_price: e.target.value })}
                          className="border rounded px-2 py-1 w-36 text-right"
                          style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                        />
                      </td>
                      <td className="py-1 pr-2 text-right" style={{ color: 'var(--text)' }}>
                        ₹ {lineTotal.toFixed(2)}
                      </td>
                      <td className="py-1 text-right">
                        <button
                          type="button"
                          className="px-2 py-1 border rounded"
                          style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
                          onClick={() => removeRow(r.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}></td>
                  <td className="py-2 pr-2 text-right font-semibold">Total</td>
                  <td className="py-2 pr-2 text-right font-semibold">₹ {grandTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-2 border rounded"
                style={{ borderColor: 'var(--border)' }}
                onClick={addRow}
              >
                + Add Row
              </button>
              <button
                type="button"
                disabled={saving}
                className="px-3 py-2 rounded border font-semibold"
                style={{ background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary-700)' }}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save Items'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showQuickAdd && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)' }}>
          <div className="w-full max-w-md p-4 rounded border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold mb-2">Quick Add Item</h2>
            <form onSubmit={handleQuickAddSubmit} className="space-y-3">
              <div>
                <label className="text-sm">Name</label>
                <input
                  className="w-full border rounded px-2 py-1"
                  style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                  value={quickAdd.name}
                  onChange={(e) => setQuickAdd(q => ({ ...q, name: e.target.value }))}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">SKU</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    value={quickAdd.sku}
                    onChange={(e) => setQuickAdd(q => ({ ...q, sku: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm">UOM</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    value={quickAdd.uom}
                    onChange={(e) => setQuickAdd(q => ({ ...q, uom: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">Category</label>
                  <select
                    className="w-full border rounded px-2 py-1"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    value={quickAdd.category}
                    onChange={(e) => setQuickAdd(q => ({ ...q, category: e.target.value }))}
                  >
                    <option>Stationery</option>
                    <option>House Keeping</option>
                    <option>IT</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm">Reorder Level</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded px-2 py-1"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    value={quickAdd.reorder_level}
                    onChange={(e) => setQuickAdd(q => ({ ...q, reorder_level: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <button type="button" className="px-3 py-2 border rounded" style={{ borderColor: 'var(--border)' }} onClick={() => setShowQuickAdd(false)}>Cancel</button>
                <button type="submit" className="px-3 py-2 rounded border font-semibold" style={{ background: '#8c211e', color: '#fff', borderColor: '#721a18' }}>Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseItems;
